import { Input, Spinner } from '@heroui/react';
import { Eye, EyeOff } from 'lucide-react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useHeroToast } from '@/hooks';
import { useUserState } from '@/state';

import { ReactComponent as Logo } from '../../../assets/logo-hs.svg';
import { axiosInstance } from '../../../axios';
import {
  AuthCard,
  Content,
  Form,
  FormSection,
  LoginHeader,
  MainContainer,
  PrimaryButton,
  Title,
} from '../auth.styled';
import { AuthFooterBar } from '../components/AuthFooterBar';
import { PasswordFormOptions, SetPasswordEntity, setPasswordFormSchema } from './SetPassword.schema';

// Wydzielony komponent dla toggle hasła
const PasswordToggle: FC<{ visible: boolean; onToggle: () => void }> = ({ visible, onToggle }) => {
  const Icon = visible ? Eye : EyeOff;
  return <Icon className="cursor-pointer" strokeWidth={1.5} onClick={onToggle} />;
};

export const SetPassword: FC = () => {
  const { t } = useTranslation();
  const { currentUser, logout } = useUserState();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showError, showSuccess } = useHeroToast();

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<SetPasswordEntity>({
    ...PasswordFormOptions,
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const handleSetPassword = async (data: SetPasswordEntity): Promise<void> => {
    const result = setPasswordFormSchema.safeParse(data);

    if (!result.success) {
      showError(t('firebaseErrors.default'));
      return;
    }

    setIsLoading(true);

    try {
      // Zmiana WŁASNEGO hasła (bez tokena zaproszenia) — backend wymaga Bearer,
      // który axiosInstance dokleja automatycznie, i wymusza uid === uid dzwoniącego.
      await axiosInstance.post('/v3-setUserPassword', {
        uid: currentUser?.uid,
        newPassword: result.data.password,
      });

      // firstLogin flow no longer needed - directly logout
      logout();
      showSuccess(t('common.success'));
    } catch {
      showError(t('firebaseErrors.default'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputProps = {
    labelPlacement: 'outside' as const,
    radius: 'sm' as const,
    variant: 'bordered' as const,
    size: 'lg' as const,
  };

  return (
    <MainContainer>
      <AuthCard $centered>
        <FormSection>
          <LoginHeader>
            <Logo />
          </LoginHeader>

          <Title>{t('auth.setPassword.title')}</Title>
          <Content>
            {t('auth.setPassword.firstTimeMessage')}
            {' '}
            {t('auth.setPassword.subtitle')}
          </Content>

          <Form onSubmit={handleSubmit(handleSetPassword)} noValidate>
            <Input
              {...inputProps}
              type={isPasswordVisible ? 'text' : 'password'}
              placeholder={t('auth.form.passwordPlaceholder')}
              isInvalid={!!errors.password}
              errorMessage={errors.password?.message}
              endContent={(
                <PasswordToggle
                  visible={isPasswordVisible}
                  onToggle={() => setIsPasswordVisible((v) => !v)}
                />
                  )}
              {...register('password')}
            />

            <Input
              {...inputProps}
              type={isConfirmPasswordVisible ? 'text' : 'password'}
              placeholder={t('auth.form.confirmPasswordPlaceholder')}
              isInvalid={!!errors.confirmPassword}
              errorMessage={errors.confirmPassword?.message}
              endContent={(
                <PasswordToggle
                  visible={isConfirmPasswordVisible}
                  onToggle={() => setIsConfirmPasswordVisible((v) => !v)}
                />
                  )}
              {...register('confirmPassword')}
            />

            <PrimaryButton type="submit" disabled={isLoading}>
              {isLoading && <Spinner size="sm" variant="spinner" classNames={{ circle1: 'border-b-white', circle2: 'border-b-white' }} />}
              {t('auth.setPassword.submitButton')}
            </PrimaryButton>

          </Form>
        </FormSection>
      </AuthCard>

      <AuthFooterBar />
    </MainContainer>
  );
};
